/* eslint-disable max-lines */
import { breakPolicyRuleResponseSchema } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { base64ToBlob } from "@src/appV2/lib/files";
import { useUploadShiftTimecardToS3 } from "@src/appV2/redesign/WorkerShifts/UnverifiedShifts/UploadTimesheet/useUploadShiftTimecardToS3";
import { useSubmitCaliforniaShiftTimeSheet } from "@src/appV2/Shifts/Shift/api/useSubmitCaliforniaShiftTimeSheet";
import { useSubmitShiftTimeSheet } from "@src/appV2/Shifts/Shift/api/useSubmitShiftTimeSheet";
import { type BreakPolicy, type Shift } from "@src/appV2/Shifts/Shift/types";
import axios from "axios";
import { useCallback, useMemo } from "react";

import { type TimesheetStep } from "../../../ShiftDetails/Timekeeping/hooks/useTimesheetStep";
import { type UploadShiftTimecardToS3Response } from "../../../UnverifiedShifts/types";
import {
  createSubmitTimesheetFormSchema,
  type SubmitTimesheetFormValues,
} from "../schemas/submitTimesheetFormSchema";

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line no-barrel-files/no-barrel-files
export type { SubmitTimesheetFormValues } from "../schemas/submitTimesheetFormSchema";

export const BREAK_AUTHORIZATION_REASON_REQUIRED_ERROR_CODE = "BREAK_AUTHORIZATION_REASON_REQUIRED";

interface UseTimesheetSubmissionParams {
  shift?: Shift;
  breakPolicy?: BreakPolicy;
  isCaliforniaFacility: boolean;
  currentStep?: TimesheetStep;
  isQrCodeSubmission?: boolean;
  isUnverifiedTimesheetFlow?: boolean;
  isSkipWorkplaceSignatureFlow?: boolean;
  workerId?: string;
  workplaceId?: string;
  onSuccess: () => void;
  onError: (errorMessage: string) => void;
  onBreakAuthorizationReasonRequired?: (
    data: SubmitTimesheetFormValues,
    rejectionPolicy: BreakPolicy
  ) => void;
  actualClockInOut?: { start: string; end: string };
  hasPendingHoursAdjustment?: boolean;
}

interface HandleSubmitOptions {
  // When true, BREAK EWTR is attached even when the shift is evaluated as too short on the frontend, because backend requires it
  forceAttachBreakAuthorizationReason?: boolean;
}

export function useTimesheetSubmission({
  shift,
  breakPolicy,
  isCaliforniaFacility,
  currentStep,
  isQrCodeSubmission = false,
  isUnverifiedTimesheetFlow = false,
  isSkipWorkplaceSignatureFlow = false,
  workerId,
  workplaceId,
  onSuccess,
  onError,
  onBreakAuthorizationReasonRequired,
  actualClockInOut,
  hasPendingHoursAdjustment = false,
}: UseTimesheetSubmissionParams) {
  const { mutateAsync: uploadToS3, isLoading: isUploading } = useUploadShiftTimecardToS3({
    meta: {
      userErrorMessage: "Failed to upload signature. Please try again.",
    },
  });

  const { mutateAsync: submitTimesheet, isLoading: isSubmitting } = useSubmitShiftTimeSheet();
  const { mutateAsync: submitCaliforniaTimesheet, isLoading: isCaliforniaSubmitting } =
    useSubmitCaliforniaShiftTimeSheet();

  const formSchema = useMemo(
    () =>
      createSubmitTimesheetFormSchema({
        isCaliforniaFacility,
        currentStep,
        isQrCodeSubmission,
        isUnverifiedTimesheetFlow,
        isSkipWorkplaceSignatureFlow,
        hasPendingHoursAdjustment,
      }),
    [
      isCaliforniaFacility,
      currentStep,
      isQrCodeSubmission,
      isUnverifiedTimesheetFlow,
      isSkipWorkplaceSignatureFlow,
      hasPendingHoursAdjustment,
    ]
  );

  const submitCaliforniaTimecard = useCallback(
    async (data: SubmitTimesheetFormValues, options: HandleSubmitOptions = {}) => {
      if (
        !shift?._id ||
        !data.hcpName ||
        !data.hcpSignature ||
        !data.facilityEmployeeName ||
        !data.workerStation ||
        !data.workerNursingAssignment
      ) {
        onError("Missing required data for California submission");
        return;
      }

      // Get timecard either from data (QR code) or by uploading signature
      let facilityTimecard: UploadShiftTimecardToS3Response;
      if (data.timecard) {
        facilityTimecard = data.timecard;
      } else if (data.facilityEmployeeSignature) {
        const facilitySignatureBlob = await base64ToBlob(data.facilityEmployeeSignature);
        facilityTimecard = await uploadToS3({
          shiftId: shift._id,
          type: facilitySignatureBlob.type,
          fileBlob: facilitySignatureBlob,
        });
      } else {
        onError("Missing required data for California submission");
        return;
      }

      // Upload HCP signature to S3
      const hcpSignatureBlob = await base64ToBlob(data.hcpSignature);
      const hcpTimecard = await uploadToS3({
        shiftId: shift._id,
        type: hcpSignatureBlob.type,
        fileBlob: hcpSignatureBlob,
      });

      // Submit California timesheet with dual signatures
      await submitCaliforniaTimesheet({
        clockInOut: {
          start: data.clockInTime.toISOString(),
          end: data.clockOutTime.toISOString(),
        },
        lunchInOut:
          data.breakStartTime && data.breakEndTime
            ? {
                start: data.breakStartTime.toISOString(),
                end: data.breakEndTime.toISOString(),
              }
            : undefined,
        shiftId: shift._id,
        facilityEmployeeName: data.facilityEmployeeName,
        hcpName: data.hcpName,
        stationWingUnitFloor: data.workerStation,
        nursingServiceAssignment: data.workerNursingAssignment,
        hcpTimecard,
        facilityTimecard,
        breakPolicy,
        forceAttachBreakAuthorizationReason: options.forceAttachBreakAuthorizationReason,
        breakAuthorizationReason: data.breakAuthorizationReason,
        missedPunches: data.missedPunches,
        actualClockInOut: isDefined(actualClockInOut)
          ? { start: actualClockInOut.start, end: actualClockInOut.end }
          : undefined,
        fullShiftExtraWorkedTimeReason: data.fullShiftExtraWorkedTimeReason,
      });
    },
    [shift, uploadToS3, submitCaliforniaTimesheet, onError, breakPolicy, actualClockInOut]
  );

  const submitRegularTimecard = useCallback(
    async (data: SubmitTimesheetFormValues, options: HandleSubmitOptions = {}) => {
      if (!isDefined(shift?._id)) {
        return;
      }

      // Get timecard either from data (QR code) or by uploading signature
      let timecard: UploadShiftTimecardToS3Response;
      if (data.timecard) {
        ({ timecard } = data);
      } else if (data.facilityEmployeeSignature) {
        const signatureBlob = await base64ToBlob(data.facilityEmployeeSignature);
        timecard = await uploadToS3({
          shiftId: shift._id,
          type: signatureBlob.type,
          fileBlob: signatureBlob,
        });
      } else {
        onError("Missing required data for submission");
        return;
      }

      await submitTimesheet({
        clockInOut: {
          start: data.clockInTime.toISOString(),
          end: data.clockOutTime.toISOString(),
        },
        lunchInOut:
          data.breakStartTime && data.breakEndTime
            ? {
                start: data.breakStartTime.toISOString(),
                end: data.breakEndTime.toISOString(),
              }
            : undefined,
        shiftId: shift._id,
        signedBy: data.facilityEmployeeName,
        timecard,
        breakPolicy,
        forceAttachBreakAuthorizationReason: options.forceAttachBreakAuthorizationReason,
        breakAuthorizationReason: data.breakAuthorizationReason,
        missedPunches: data.missedPunches,
        actualClockInOut: isDefined(actualClockInOut)
          ? { start: actualClockInOut.start, end: actualClockInOut.end }
          : undefined,
        fullShiftExtraWorkedTimeReason: data.fullShiftExtraWorkedTimeReason,
      });
    },
    [shift, uploadToS3, submitTimesheet, breakPolicy, onError, actualClockInOut]
  );

  const submitCaliforniaTimecardWithoutHCFSignature = useCallback(
    async (data: SubmitTimesheetFormValues, options: HandleSubmitOptions = {}) => {
      if (!isDefined(shift?._id) || !data.hcpSignature) {
        onError("Missing required data for California submission");
        return;
      }

      // Upload HCP signature to S3
      const hcpSignatureBlob = await base64ToBlob(data.hcpSignature);
      const hcpTimecard = await uploadToS3({
        shiftId: shift._id,
        type: hcpSignatureBlob.type,
        fileBlob: hcpSignatureBlob,
      });

      await submitCaliforniaTimesheet({
        clockInOut: {
          start: data.clockInTime.toISOString(),
          end: data.clockOutTime.toISOString(),
        },
        lunchInOut:
          data.breakStartTime && data.breakEndTime
            ? {
                start: data.breakStartTime.toISOString(),
                end: data.breakEndTime.toISOString(),
              }
            : undefined,
        shiftId: shift._id,
        facilityEmployeeName: data.facilityEmployeeName,
        hcpName: data.hcpName ?? "",
        stationWingUnitFloor: data.workerStation,
        nursingServiceAssignment: data.workerNursingAssignment,
        hcpTimecard,
        breakPolicy,
        forceAttachBreakAuthorizationReason: options.forceAttachBreakAuthorizationReason,
        breakAuthorizationReason: data.breakAuthorizationReason,
        missedPunches: data.missedPunches,
        actualClockInOut: isDefined(actualClockInOut)
          ? { start: actualClockInOut.start, end: actualClockInOut.end }
          : undefined,
        fullShiftExtraWorkedTimeReason: data.fullShiftExtraWorkedTimeReason,
      });
    },
    [shift, uploadToS3, submitCaliforniaTimesheet, onError, breakPolicy, actualClockInOut]
  );

  const submitTimesheetWithoutSignature = useCallback(
    async (data: SubmitTimesheetFormValues, options: HandleSubmitOptions = {}) => {
      if (!isDefined(shift?._id)) {
        onError("Missing required data for submission");
        return;
      }

      await submitTimesheet({
        clockInOut: {
          start: data.clockInTime.toISOString(),
          end: data.clockOutTime.toISOString(),
        },
        lunchInOut:
          data.breakStartTime && data.breakEndTime
            ? {
                start: data.breakStartTime.toISOString(),
                end: data.breakEndTime.toISOString(),
              }
            : undefined,
        shiftId: shift._id,
        breakPolicy,
        forceAttachBreakAuthorizationReason: options.forceAttachBreakAuthorizationReason,
        breakAuthorizationReason: data.breakAuthorizationReason,
        missedPunches: data.missedPunches,
        actualClockInOut: isDefined(actualClockInOut)
          ? { start: actualClockInOut.start, end: actualClockInOut.end }
          : undefined,
        fullShiftExtraWorkedTimeReason: data.fullShiftExtraWorkedTimeReason,
      });
    },
    [shift, submitTimesheet, breakPolicy, actualClockInOut, onError]
  );

  const handleSubmit = useCallback(
    async (data: SubmitTimesheetFormValues, options: HandleSubmitOptions = {}) => {
      logEvent(APP_V2_USER_EVENTS.TIMESHEET_SUBMISSION_STARTED, {
        shiftId: shift?._id,
        workerId,
        workplaceId,
        isCaliforniaFacility,
        isUnverifiedTimesheetFlow,
        isSkipWorkplaceSignatureFlow,
        isResubmit: options.forceAttachBreakAuthorizationReason ?? false,
      });

      try {
        if (isCaliforniaFacility) {
          const submitFunction = isSkipWorkplaceSignatureFlow
            ? submitCaliforniaTimecardWithoutHCFSignature
            : submitCaliforniaTimecard;
          await submitFunction(data, options);
        } else {
          const submitFunction = isSkipWorkplaceSignatureFlow
            ? submitTimesheetWithoutSignature
            : submitRegularTimecard;
          await submitFunction(data, options);
        }

        onSuccess();
      } catch (error: unknown) {
        const submittedReason = data.breakAuthorizationReason;
        const hasSubmittedReason = isDefined(submittedReason) && submittedReason.trim().length > 0;

        const rejectionPolicy = getBreakRejectionPolicy(error);
        if (
          isDefined(onBreakAuthorizationReasonRequired) &&
          isDefined(rejectionPolicy) &&
          !hasSubmittedReason
        ) {
          logEvent(APP_V2_USER_EVENTS.TIMESHEET_BREAK_AUTHORIZATION_REASON_REQUIRED, {
            shiftId: shift?._id,
            workerId,
            workplaceId,
            isCaliforniaFacility,
            isUnverifiedTimesheetFlow,
            isSkipWorkplaceSignatureFlow,
            isResubmit: options.forceAttachBreakAuthorizationReason ?? false,
          });
          onBreakAuthorizationReasonRequired(data, rejectionPolicy);
          return;
        }

        onError(
          getApiErrorMessage(error) ??
            "Something went wrong while submitting your timesheet. Please try again."
        );
      }
    },
    [
      isSkipWorkplaceSignatureFlow,
      submitTimesheetWithoutSignature,
      submitCaliforniaTimecardWithoutHCFSignature,
      isCaliforniaFacility,
      submitCaliforniaTimecard,
      submitRegularTimecard,
      isUnverifiedTimesheetFlow,
      shift?._id,
      workerId,
      workplaceId,
      onSuccess,
      onError,
      onBreakAuthorizationReasonRequired,
    ]
  );

  return {
    handleSubmit,
    submitCaliforniaTimecardWithoutHCFSignature,
    isLoading: isUploading || isSubmitting || isCaliforniaSubmitting,
    formSchema,
  };
}

function getApiErrorMessage(error: unknown): string | undefined {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    const message = data?.message;

    if (typeof message === "string" && message.trim().length > 0) {
      return message;
    }
  }

  return undefined;
}

function getBreakRejectionPolicy(error: unknown): BreakPolicy | undefined {
  if (!axios.isAxiosError(error) || error.response?.status !== 400) {
    return undefined;
  }

  const data = error.response?.data as { error?: unknown } | undefined;
  if (data?.error !== BREAK_AUTHORIZATION_REASON_REQUIRED_ERROR_CODE) {
    return undefined;
  }

  const parsed = breakPolicyRuleResponseSchema.safeParse(error.response?.data);
  return parsed.success ? parsed.data : undefined;
}
/* eslint-enable max-lines */
